import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageHeader, TierBadge, EmptyState } from "../../components/bits";
import { Scale, Trophy } from "lucide-react";

const SORTS = [
  { key: "impact", label: "نقاط الأثر", field: "impact_score" },
  { key: "hours", label: "إجمالي الساعات", field: "total_hours" },
  { key: "pct", label: "نسبة التمكين", field: "pct" },
];

export default function AdminCompare() {
  const [schools, setSchools] = useState(null);
  const [sort, setSort] = useState("impact");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    api.get("/admin/schools").then((r) => setSchools(r.data)).catch(() => {});
  }, []);

  const field = SORTS.find((s) => s.key === sort).field;
  const sorted = useMemo(() => {
    if (!schools) return [];
    return [...schools].sort((a, b) => (b[field] || 0) - (a[field] || 0));
  }, [schools, field]);

  const toggle = (id) =>
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const chosen = sorted.filter((s) => selected.includes(s.school.id));

  const metrics = [
    ["إجمالي الطلاب", (s) => s.total_students],
    ["المتطوعون الفعليون", (s) => s.unique_students],
    ["نسبة التمكين %", (s) => s.pct || 0],
    ["الفرص التطوعية", (s) => s.opportunities],
    ["إجمالي الساعات", (s) => s.total_hours],
    ["نقاط الأثر", (s) => s.impact_score],
  ];

  if (!schools) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  return (
    <div data-testid="admin-compare-page">
      <PageHeader title="الإحصاءات والمقارنات" sub="ترتيب المدارس ومقارنة الأداء — اختر مدرستين أو أكثر للمقارنة جنباً إلى جنب">
        <select data-testid="compare-sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
          className="rounded-xl border border-brd bg-surface px-3 py-2.5 text-sm outline-none">
          {SORTS.map((s) => <option key={s.key} value={s.key}>ترتيب حسب {s.label}</option>)}
        </select>
      </PageHeader>

      <div className="grid lg:grid-cols-[1fr_380px] gap-5">
        <div className="bg-surface rounded-3xl border border-brd shadow-md p-5 order-2 lg:order-1">
          <h3 className="font-head font-bold text-sm mb-4 flex items-center gap-2">
            <Scale className="w-4 h-4 text-coral-600" /> مقارنة المدارس المختارة ({chosen.length})
          </h3>
          {chosen.length < 2 ? (
            <EmptyState testid="compare-empty" text="اختر مدرستين أو أكثر من قائمة الترتيب للمقارنة" />
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm min-w-[520px]" data-testid="compare-table">
                <thead>
                  <tr className="bg-turq-900 text-white">
                    <th className="text-right px-3 py-2.5 font-head text-xs">المؤشر</th>
                    {chosen.map((s) => (
                      <th key={s.school.id} className="text-right px-3 py-2.5 font-head text-xs">
                        <div>{s.school.name}</div>
                        <div className="mt-1"><TierBadge tier={s.tier} /></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {metrics.map(([label, fn]) => {
                    const vals = chosen.map(fn);
                    const best = Math.max(...vals);
                    return (
                      <tr key={label} className="border-b border-brd/50 last:border-0">
                        <td className="px-3 py-2.5 font-bold text-[12px]">{label}</td>
                        {chosen.map((s, i) => (
                          <td key={s.school.id}
                            className={`px-3 py-2.5 font-head font-black ${vals[i] === best && best > 0 ? "text-coral-600" : "text-ink"}`}>
                            {vals[i]}
                            {vals[i] === best && best > 0 && <Trophy className="w-3 h-3 inline mr-1 text-coral-600" />}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-surface rounded-3xl border border-brd shadow-md p-5 order-1 lg:order-2">
          <h3 className="font-head font-bold text-sm mb-4">ترتيب المدارس — حسب {SORTS.find((s) => s.key === sort).label}</h3>
          <div className="space-y-2 max-h-[520px] overflow-auto">
            {sorted.map((s, i) => (
              <label key={s.school.id} data-testid={`compare-row-${s.school.id.slice(0, 8)}`}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 cursor-pointer transition-colors ${
                  selected.includes(s.school.id) ? "bg-coral-100 dark:bg-surface2" : "bg-bg hover:bg-surface2/60"}`}>
                <input type="checkbox" checked={selected.includes(s.school.id)} onChange={() => toggle(s.school.id)}
                  className="w-4 h-4 rounded accent-[#FF7A59]" />
                <span className={`w-6 h-6 rounded-full text-[11px] font-black flex items-center justify-center text-white shrink-0 ${i < 3 ? "bg-coral-600" : "bg-turq-900"}`}>
                  {i + 1}
                </span>
                <span className="flex-1 text-[12.5px] font-bold truncate">{s.school.name}</span>
                <span className="font-head font-black text-sm text-turq-900 dark:text-turq-300 shrink-0">{s[field] || 0}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
